/**
 * Shared click-to-edit table bits for the AD Leadership & Coaches configurator.
 * Contacts-table style: cells render as PLAIN TEXT; click a cell → input/select
 * in place; blur/Enter = autosave, Esc = cancel. All writes go to /api/ad-staff.
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Loader2, Clock, Trash2, ChevronRight, ChevronDown, Check, X, Upload, ArrowUp, ArrowDown, Search } from 'lucide-react';

export const API_URL = process.env.REACT_APP_BACKEND_URL;

export function useExistingStaff(orgId, reloadKey) {
  const [people, setPeople] = useState([]);
  useEffect(() => {
    if (!orgId) return;
    axios.get(`${API_URL}/api/ol-canonical/coaches/${orgId}`, { params: { limit: 500 } })
      .then(r => {
        const seen = new Set(); const list = [];
        for (const c of (r.data?.data || [])) {
          const id = c.id;
          const name = c.title || `${c.first_name || ''} ${c.last_name || ''}`.trim();
          if (!id || !name || seen.has(id)) continue;
          seen.add(id); list.push({ id, name, photo_url: c.photo_url || '' });
        }
        list.sort((a, b) => a.name.localeCompare(b.name));
        setPeople(list);
      }).catch(() => setPeople([]));
  }, [orgId, reloadKey]);
  return people;
}

const editInput = (isDark) =>
  `w-full text-sm rounded px-1 py-0.5 -mx-1 border outline-none ${isDark ? 'bg-slate-700 border-amber-500/50 text-white' : 'bg-white border-amber-400'}`;
const cellHover = (isDark) =>
  `block truncate cursor-text rounded px-1 -mx-1 py-0.5 min-h-[22px] ${isDark ? 'hover:bg-slate-700/50' : 'hover:bg-slate-100'}`;

/** Plain text → click → input; blur/Enter save, Esc cancel. */
export function InlineText({ value, onSave, placeholder = '—', type = 'text', canEdit = true, isDark, testid, suggested }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || '');
  useEffect(() => { setV(value || ''); }, [value]);
  if (!canEdit) return <span className="text-slate-400 text-xs">—</span>;
  if (!editing) {
    if (!value && suggested) {
      return (
        <span className="inline-flex items-center gap-1" data-testid={testid}>
          <span onClick={() => { setV(suggested); setEditing(true); }} title="From Family Directory"
                className={`${cellHover(isDark)} italic ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            {suggested}
          </span>
          <button title="Use this (from Family Directory)" data-testid={testid ? `${testid}-accept` : undefined}
                  onClick={() => onSave(suggested)} className="text-emerald-500 hover:text-emerald-400 shrink-0">
            <Check className="h-3.5 w-3.5" />
          </button>
        </span>
      );
    }
    return (
      <span onClick={() => setEditing(true)} data-testid={testid}
            className={`${cellHover(isDark)} ${value ? '' : 'text-slate-400 italic'}`}>
        {value || placeholder}
      </span>
    );
  }
  const commit = async () => { setEditing(false); if ((v || '') !== (value || '')) await onSave(v); };
  return (
    <input autoFocus type={type} value={v} data-testid={testid ? `${testid}-input` : undefined}
           className={editInput(isDark)} onChange={e => setV(e.target.value)} onBlur={commit}
           onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setV(value || ''); setEditing(false); } }} />
  );
}

/** Plain text → click → select; change saves, Esc cancels. */
export function InlineSelect({ value, label, options, onSave, placeholder = '—', isDark, testid }) {
  const [editing, setEditing] = useState(false);
  if (!editing) {
    return (
      <span onClick={() => setEditing(true)} data-testid={testid}
            className={`${isDark ? 'hover:bg-slate-700/50' : 'hover:bg-slate-100'} block truncate cursor-pointer rounded px-1 -mx-1 py-0.5 min-h-[22px] ${label ? '' : 'text-slate-400 italic'}`}>
        {label || placeholder}
      </span>
    );
  }
  return (
    <select autoFocus value={value || ''} data-testid={testid ? `${testid}-input` : undefined} className={editInput(isDark)}
            onBlur={() => setEditing(false)}
            onChange={async e => { setEditing(false); if (e.target.value !== (value || '')) await onSave(e.target.value); }}
            onKeyDown={e => { if (e.key === 'Escape') setEditing(false); }}>
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function Avatar({ person, isDark }) {
  if (person?.photo_url) return <img src={person.photo_url} alt="" className="h-6 w-6 rounded-full object-cover shrink-0" />;
  const initials = (person?.name || '').split(/\s+/).filter(Boolean).map(s => s[0]).slice(0, 2).join('').toUpperCase();
  return (
    <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0 ${person ? (isDark ? 'bg-slate-600 text-slate-200' : 'bg-slate-200 text-slate-600') : (isDark ? 'bg-slate-700/60 text-slate-500' : 'bg-slate-100 text-slate-400')}`}>
      {person ? initials : '·'}
    </span>
  );
}

/** Person select (existing + "New person" first/last). Reused by cell + add-row. */
export function PersonPicker({ people, orgId, value, onPick, onCancel, isDark, testid, autoFocus }) {
  const [mode, setMode] = useState('existing');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [busy, setBusy] = useState(false);
  if (mode === 'new') {
    const save = async () => {
      if (!(first.trim() || last.trim())) return;
      setBusy(true);
      try {
        const { data } = await axios.post(`${API_URL}/api/ad-staff/${orgId}/coach`, { first_name: first.trim(), last_name: last.trim() });
        await onPick({ coach_id: data.coach.id, name: data.coach.display_name });
        setFirst(''); setLast(''); setMode('existing');
      } finally { setBusy(false); }
    };
    return (
      <div className="flex items-center gap-1">
        <input className={editInput(isDark)} placeholder="First" value={first} data-testid={`${testid}-first`} autoFocus onChange={e => setFirst(e.target.value)} />
        <input className={editInput(isDark)} placeholder="Last" value={last} data-testid={`${testid}-last`} onChange={e => setLast(e.target.value)} />
        <button disabled={busy} onClick={save} title="Save" data-testid={`${testid}-save`} className="text-emerald-500 hover:text-emerald-400 shrink-0">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}</button>
        <button onClick={() => (onCancel ? onCancel() : setMode('existing'))} title="Cancel" className="text-slate-400 hover:text-slate-300 shrink-0"><X className="h-4 w-4" /></button>
      </div>
    );
  }
  return (
    <select value={value || ''} data-testid={testid} className={editInput(isDark)} autoFocus={autoFocus}
            onBlur={() => onCancel && onCancel()}
            onChange={e => { const v = e.target.value; if (v === '__new__') { setMode('new'); return; } if (v) onPick({ coach_id: v, name: (people.find(p => p.id === v) || {}).name }); else if (onCancel) onCancel(); }}
            onKeyDown={e => { if (e.key === 'Escape' && onCancel) onCancel(); }}>
      <option value="">— select —</option>
      {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      <option value="__new__">＋ New person…</option>
    </select>
  );
}

/** Person cell: avatar + name (plain) → click → PersonPicker. */
export function PersonCell({ card, people, orgId, onPick, isDark, testid }) {
  const [editing, setEditing] = useState(false);
  const person = card.person;
  if (editing) {
    return (
      <PersonPicker people={people} orgId={orgId} isDark={isDark} value={person?.coach_id} testid={testid} autoFocus
                    onCancel={() => setEditing(false)}
                    onPick={async (p) => { setEditing(false); await onPick(p); }} />
    );
  }
  return (
    <span onClick={() => setEditing(true)} data-testid={testid}
          className={`flex items-center gap-2 cursor-pointer rounded px-1 -mx-1 py-0.5 min-h-[22px] ${isDark ? 'hover:bg-slate-700/50' : 'hover:bg-slate-100'}`}>
      <Avatar person={person} isDark={isDark} />
      <span className={`truncate ${person ? '' : 'text-slate-400 italic'}`}>{person?.name || '— assign —'}</span>
    </span>
  );
}

export function HeldBadge({ card, sportName }) {
  if (!card.held) return <span className="text-slate-400 text-xs">—</span>;
  return (
    <span title={`Website updates when the ${card.held_season} ${sportName || ''} schedule is loaded.`} data-testid={`held-${card.slug}`}
          className="inline-flex items-center text-amber-500" >
      <Clock className="h-3.5 w-3.5" />
    </span>
  );
}

export function ExpandChevron({ open, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="text-slate-400 hover:text-slate-200">
      {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
    </button>
  );
}

export function RemoveBtn({ onClick, testid }) {
  return (
    <button onClick={onClick} title="Remove" data-testid={testid}
            className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
}

export function HeadshotBio({ card, orgId, isDark, onDone }) {
  const coachId = card.person?.coach_id;
  const [busy, setBusy] = useState(false);
  const [bio, setBio] = useState(card.person?.bio || '');
  useEffect(() => { setBio(card.person?.bio || ''); }, [card.person?.bio]);
  if (!coachId) return <p className="text-xs text-slate-400 italic">Assign a person first to add a headshot or bio.</p>;
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append('file', f); await axios.post(`${API_URL}/api/ad-staff/${orgId}/coach/${coachId}/headshot`, fd); onDone && onDone(); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-start gap-4 py-1">
      <div className="flex flex-col items-center gap-1">
        <Avatar person={card.person} isDark={isDark} />
        <label className={`text-[11px] cursor-pointer inline-flex items-center gap-1 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} data-testid={`headshot-upload-${card.slug}`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Upload
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
      </div>
      <div className="flex-1 min-w-[240px]">
        <label className="text-[11px] text-slate-400">Bio</label>
        <textarea rows={2} value={bio} placeholder="Short bio…" data-testid={`bio-${card.slug}`}
                  className={`w-full text-sm rounded px-2 py-1 border ${isDark ? 'bg-slate-700/60 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                  onChange={e => setBio(e.target.value)}
                  onBlur={async () => { if ((bio || '') !== (card.person?.bio || '')) { await axios.post(`${API_URL}/api/ad-staff/${orgId}/coach`, { coach_id: coachId, bio }); onDone && onDone(); } }} />
      </div>
    </div>
  );
}

/** Sortable column header. */
export function SortHeader({ label, sortKey, sort, onSort, isDark, className = '' }) {
  const active = sort?.key === sortKey;
  return (
    <th className={`${thCls(isDark)} ${className} cursor-pointer select-none`} onClick={() => onSort(sortKey)} data-testid={`sort-${sortKey}`}>
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (sort.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </span>
    </th>
  );
}

export function useSort(initKey = null) {
  const [sort, setSort] = useState({ key: initKey, dir: 'asc' });
  const onSort = (key) => setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  const sortCards = (cards) => {
    if (!sort.key) return cards;  // default = backend registry order (HC first / President first)
    const get = (c) => sort.key === 'person' ? (c.person?.name || '~').toLowerCase() : (c.role_title || '').toLowerCase();
    const out = [...cards].sort((a, b) => get(a).localeCompare(get(b)));
    return sort.dir === 'asc' ? out : out.reverse();
  };
  return { sort, onSort, sortCards };
}

export function SearchBox({ value, onChange, isDark, placeholder = 'Search person, role, email…', testid = 'ad-staff-search' }) {
  return (
    <div className={`flex items-center gap-2 rounded-md border px-2 h-9 max-w-xs ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-300'}`}>
      <Search className="h-4 w-4 text-slate-400" />
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
             className={`flex-1 bg-transparent text-sm outline-none ${isDark ? 'text-white placeholder:text-slate-500' : ''}`} />
      {value && <button onClick={() => onChange('')} className="text-slate-400 hover:text-slate-200"><X className="h-3.5 w-3.5" /></button>}
    </div>
  );
}

export function matchesSearch(card, q) {
  if (!q) return true;
  const s = q.toLowerCase();
  return [(card.role_title || ''), (card.person?.name || ''), (card.person?.email || '')].some(x => x.toLowerCase().includes(s));
}

export function TableFooter({ count, isDark }) {
  return (
    <p className={`text-xs mt-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} data-testid="ad-staff-footer">
      {count} position{count === 1 ? '' : 's'} · Click any field to edit · auto-saves
    </p>
  );
}

export function Collapsible({ title, titleNode, count, children, isDark, defaultOpen = true, testid, headerRight }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`rounded-lg border ${isDark ? 'border-slate-700' : 'border-slate-200'}`} data-testid={testid}>
      <div className={`w-full flex items-center justify-between px-4 py-2.5 ${isDark ? 'text-white' : 'text-slate-900'}`}>
        <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-left" data-testid={`${testid}-header`}>
          {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
          <span className="font-semibold text-lg flex items-center gap-2">
            {titleNode || title}
            <span className={`text-xs font-normal px-2 py-0.5 rounded-full ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>{count}</span>
          </span>
        </button>
        {headerRight && <div className="flex items-center gap-1">{headerRight}</div>}
      </div>
      {open && <div className="px-3 pb-3 overflow-x-auto">{children}</div>}
    </div>
  );
}

export const thCls = (isDark) =>
  `text-left text-[11px] font-semibold uppercase tracking-wide px-2 py-1.5 sticky top-0 z-10 ${isDark ? 'text-slate-400 bg-slate-800' : 'text-slate-500 bg-white'}`;
export const tdCls = 'px-2 py-1 align-middle';

/** Add-role trailing row (role select + person picker → create). */
export function AddRoleRow({ roles, people, orgId, isDark, colSpan, onCreate, allowNewRole, newRoleScope = 'team', newRoleArea = null, testid }) {
  const [role, setRole] = useState('');
  const [busy, setBusy] = useState(false);
  const pickRole = async (v) => {
    if (v === '__newrole__') {
      const label = window.prompt('New role label (e.g. Team Photographer):');
      if (!label) { setRole(''); return; }
      const payload = { label, scope: newRoleScope };
      if (newRoleScope === 'org' && newRoleArea) payload.area = newRoleArea;
      const { data } = await axios.post(`${API_URL}/api/ad-staff/${orgId}/registry-role`, payload);
      setRole(data.role.code); return;
    }
    setRole(v);
  };
  return (
    <tr data-testid={testid}>
      <td className="px-2 py-1" colSpan={colSpan}>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-400">＋</span>
          <select value={role} className={`${editInput(isDark)} max-w-[170px]`} data-testid={`${testid}-role`} onChange={e => pickRole(e.target.value)}>
            <option value="">Add role…</option>
            {roles.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
            {allowNewRole && <option value="__newrole__">＋ New role…</option>}
          </select>
          {role && (
            <div className="max-w-[220px] w-full">
              <PersonPicker people={people} orgId={orgId} isDark={isDark} testid={`${testid}-person`}
                            onPick={async ({ coach_id }) => { setBusy(true); try { await onCreate({ role, coach_id }); setRole(''); } finally { setBusy(false); } }} />
            </div>
          )}
          {busy && <Loader2 className="h-4 w-4 animate-spin text-amber-500" />}
        </div>
      </td>
    </tr>
  );
}
